/**
 * STLExporter - Export the generated 3D meshes as a binary STL
 *
 * Hand-rolled binary STL writer (80-byte header, uint32 triangle count,
 * then 50-byte triangle records: 12 floats + uint16 attribute).
 *
 * IMPORTANT: the generated meshes live inside the scene-rotation pivot
 * group, so each mesh is transformed by its LOCAL matrix only (never
 * matrixWorld) - the pivot's view rotation must not leak into the export.
 *
 * Units: 1 world unit = 100 mm (1 sketch px = 1 mm), so callers pass
 * scaleFactor = 100 * (scalePct / 100) to get millimetre output.
 */

import * as THREE from 'three';
import { MeshBuilder } from '../geometry/MeshBuilder.js';
import { packParts, packPartsSquare } from './ShapePacker.js';

/**
 * Number of triangles in a mesh's BufferGeometry (indexed or not)
 */
function meshTriangleCount(mesh) {
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position');
    if (!pos) return 0;
    return Math.floor((geo.index ? geo.index.count : pos.count) / 3);
}

/**
 * Build a binary STL from an array of THREE.Mesh objects.
 *
 * @param {Array<THREE.Mesh>} meshes - Meshes to export
 * @param {number} [scaleFactor=1] - Multiplier applied to every coordinate
 *   (use 100 for world units -> mm at 100% scale)
 * @returns {ArrayBuffer} Binary STL file contents
 */
export function buildSTL(meshes, scaleFactor = 1) {
    const valid = (meshes || []).filter(m => m && m.geometry && m.geometry.getAttribute('position'));

    let triCount = 0;
    for (const mesh of valid) {
        triCount += meshTriangleCount(mesh);
    }

    const buffer = new ArrayBuffer(84 + triCount * 50);
    const view = new DataView(buffer);

    // 80-byte ASCII header (padded with zeros)
    const header = 'SketchChair.js binary STL - millimetres';
    for (let i = 0; i < Math.min(header.length, 80); i++) {
        view.setUint8(i, header.charCodeAt(i));
    }
    view.setUint32(80, triCount, true);

    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const ab = new THREE.Vector3();
    const ac = new THREE.Vector3();
    const n = new THREE.Vector3();

    let offset = 84;
    for (const mesh of valid) {
        const geo = mesh.geometry;
        const pos = geo.getAttribute('position');
        const index = geo.index;

        // LOCAL matrix only (position/rotation/scale of the mesh itself);
        // deliberately ignores any parent (the rotation pivot group)
        mesh.updateMatrix();
        const matrix = mesh.matrix;

        const tris = meshTriangleCount(mesh);
        for (let t = 0; t < tris; t++) {
            const i0 = index ? index.getX(t * 3) : t * 3;
            const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1;
            const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2;

            a.fromBufferAttribute(pos, i0).applyMatrix4(matrix).multiplyScalar(scaleFactor);
            b.fromBufferAttribute(pos, i1).applyMatrix4(matrix).multiplyScalar(scaleFactor);
            c.fromBufferAttribute(pos, i2).applyMatrix4(matrix).multiplyScalar(scaleFactor);

            // Per-face normal from the transformed triangle (slicers
            // typically recompute these anyway)
            n.crossVectors(ab.subVectors(b, a), ac.subVectors(c, a));
            if (n.lengthSq() > 0) n.normalize();
            else n.set(0, 0, 0);

            view.setFloat32(offset, n.x, true);
            view.setFloat32(offset + 4, n.y, true);
            view.setFloat32(offset + 8, n.z, true);

            view.setFloat32(offset + 12, a.x, true);
            view.setFloat32(offset + 16, a.y, true);
            view.setFloat32(offset + 20, a.z, true);
            view.setFloat32(offset + 24, b.x, true);
            view.setFloat32(offset + 28, b.y, true);
            view.setFloat32(offset + 32, b.z, true);
            view.setFloat32(offset + 36, c.x, true);
            view.setFloat32(offset + 40, c.y, true);
            view.setFloat32(offset + 44, c.z, true);

            view.setUint16(offset + 48, 0, true); // attribute byte count
            offset += 50;
        }
    }

    return buffer;
}

/**
 * Assembled pose of a part in the chair, replicating EXACTLY the transform
 * Application.generateMeshesForPlane gives the generated meshes (which is
 * what the user sees), for geometry that is in sketch/profile-frame pixels
 * with a uniform 0.01 mesh scale and a z-centred extrusion (+-t/2 - the
 * buildExtrudedGeometry convention, same as the assembled meshes).
 *
 * - Base panels (horizontal planes, |normal.z| >= 0.1): the plane's
 *   quaternion frame degenerates to identity here (generateMeshesForPlane
 *   applies no rotation) and the mesh centre sits AT the plane position
 *   (the z-centred extrusion straddles the plane by +-t/2, matching the
 *   rib slots which are cut centred on the plane's z).
 * - Ribs (vertical cross-slice planes, |normal.z| < 0.1): the profile
 *   geometry lives in the PROFILE frame (X along plane.profileXAxis, Y up
 *   along world Z - see SlicePlane.getProfileWorldPos). The equivalent
 *   mesh transform is Euler('ZXY', PI/2, 0, atan2(n.y, n.x) - PI/2) at
 *   (position.x, position.y, 0): local X maps onto profileXAxis and local
 *   Y onto world Z.
 *
 * @param {Object|null} plane - Source SlicePlane ({position, normal})
 * @param {number} thicknessMM - Part thickness in mm (kept for signature
 *   stability; the pose is thickness-independent now the extrusion is
 *   centred on the plane)
 * @returns {{position: THREE.Vector3, quaternion: THREE.Quaternion,
 *   scale: number}|null} Pose in the rotation-pivot frame, or null if the
 *   plane is unknown
 */
export function computeAssembledPose(plane, thicknessMM) {
    if (!plane || !plane.position || !plane.normal) return null;

    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const isVerticalPlane = Math.abs(plane.normal.z) < 0.1;

    if (isVerticalPlane) {
        // Rib: profile frame (see doc comment above)
        const normalAngle = Math.atan2(plane.normal.y, plane.normal.x);
        quaternion.setFromEuler(
            new THREE.Euler(Math.PI / 2, 0, normalAngle - Math.PI / 2, 'ZXY'));
        position.set(plane.position.x, plane.position.y, 0);
    } else {
        // Base panel: plane quaternion frame (identity for horizontal
        // planes), z-centred extrusion straddling the plane position
        position.set(
            plane.position.x,
            plane.position.y,
            plane.position.z
        );
    }

    return { position, quaternion, scale: 0.01 };
}

// Flat parts float this far above the ground plane so they never z-fight it
const FLAT_LIFT = 0.02;

/**
 * Compensate an assembled pose for the packer's 90-degree orientation.
 *
 * packParts may pick the 90-degree-rotated orientation of a part, in which
 * case placed.part carries GENUINELY rotated polygons: rotatePart90 maps
 * (p - c) -> Rz(-90deg)(p - c) around the bbox centre c. The extruded
 * geometry then lives in that rotated frame, but computeAssembledPose maps
 * the ORIGINAL source frame - spawning the rotated geometry at the plain
 * pose showed the part 90 degrees off around its own centre (and snapping
 * into place when the preview swapped back to the real meshes).
 *
 * Undo it inside the pose: with geometry g = R(p), the corrected transform
 * is M' = M * R^-1 (R^-1 = rotate +90deg about z around c), so
 *   M'(g) = M(p) for every source point p:
 *   position' = position + quaternion * (scale * (c - Rz(+90)c))
 *   quaternion' = quaternion * Rz(+90)
 *
 * @param {{position, quaternion, scale}|null} pose - From computeAssembledPose
 * @param {boolean} rotated - Did the packer pick the rotated orientation?
 * @param {{x, y}} rotCentre - Rotation centre in polygon (sketch px) space
 * @returns {{position, quaternion, scale}|null} Corrected pose
 */
function compensatePackingRotation(pose, rotated, rotCentre) {
    if (!pose || !rotated || !rotCentre) return pose;

    const rz90 = new THREE.Quaternion()
        .setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 2);
    const c = new THREE.Vector3(rotCentre.x, rotCentre.y, 0);
    const offset = c.clone().sub(c.clone().applyQuaternion(rz90)) // c - Rz(+90)c
        .multiplyScalar(pose.scale)
        .applyQuaternion(pose.quaternion);

    return {
        position: pose.position.clone().add(offset),
        quaternion: pose.quaternion.clone().multiply(rz90),
        scale: pose.scale
    };
}

/**
 * Build flat, packed part meshes for the "Unassembled (flat parts)" 3D
 * print mode: every fabrication part (from SVGExporter.collectParts, slot
 * cuts included) laid out on one unbounded-height sheet like the 2D cut
 * pattern, each extruded to its own plane's material thickness and resting
 * on the ground plane (z = 0 .. thickness).
 *
 * Contract:
 * - Each mesh's GEOMETRY is in part-local sketch-px coordinates
 *   (untranslated by packing), extruded z in [-t/2, +t/2].
 * - Each mesh's baked transform is its packed FLAT placement in sheet
 *   millimetres: position (dx, dy, thickness/2), identity rotation - so
 *   buildSTL/computeMeshesBBox (local matrix, scaleFactor = scalePct/100)
 *   produce exactly the same flat mm output as before.
 * - mesh.userData additionally carries the two viewport poses (in world
 *   units, in the rotation-pivot frame, for meshes with scale 0.01):
 *     flatPose      - packed layout lying on the ground plane (y = groundY)
 *     assembledPose - the part's pose in the assembled chair (null when the
 *                     part carries no source plane), see computeAssembledPose
 *   These drive the assembled<->flat animation in MaterialsPanel.
 *
 * Coordinates are millimetres (1 sketch px = 1 mm), so callers pass the
 * resulting meshes to buildSTL with scaleFactor = scalePct / 100.
 *
 * @param {Array<{name, polygons, thickness, plane}>} parts - From collectParts
 * @param {Object} [options]
 * @param {number} [options.sheetWidth=800] - Packing sheet width in mm
 * @param {number} [options.gap=10] - Clearance between parts in mm
 * @param {number} [options.margin=10] - Sheet border margin in mm
 * @param {number} [options.defaultThickness=5] - Fallback part thickness mm
 * @param {number} [options.groundY=-5] - Ground plane height in world units
 *   (used for flatPose only)
 * @returns {{meshes: Array<THREE.Mesh>, widthMM: number, heightMM: number,
 *   partCount: number}}
 */
export function buildFlatPartMeshes(parts, options = {}) {
    const {
        sheetWidth = null,
        gap = 10,
        margin = 10,
        defaultThickness = 5,
        groundY = -5
    } = options;

    // These parts are headed for a printer bed, not a cut sheet, so by default
    // pack them into the most SQUARE footprint available (packPartsSquare)
    // rather than pinning them to a material width and growing a long strip.
    // A caller that does want a fixed width (the tests, and any sheet-bound
    // use) can still pass sheetWidth and get the old fixed-width packing.
    const { placed, widthMM, heightMM } = sheetWidth
        ? packParts(parts, { sheetWidth, gap, margin })
        : packPartsSquare(parts, { gap, margin });

    const meshBuilder = new MeshBuilder();
    const meshes = [];

    // The flat layout lies in the XZ plane on the ground: sheet +x -> world
    // +x, sheet +y -> world -z, thickness -> world +y. That is a -90deg
    // rotation about X (local z, the extrusion axis, maps to world +y).
    const flatQuaternion = new THREE.Quaternion()
        .setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

    for (const { part, dx, dy, rotated, rotCentre } of placed) {
        const thickness = (typeof part.thickness === 'number' && part.thickness > 0)
            ? part.thickness
            : defaultThickness;

        // Geometry in part-local sketch px; the packed offset goes into the
        // mesh transform instead
        const geometry = meshBuilder.buildExtrudedGeometry(part.polygons, thickness);
        if (!geometry) continue;

        // Same material + lighting response as the assembled chair meshes
        // (MeshBuilder.createExtrudedMesh), so the flat-parts preview reads
        // identically in the viewport. STL export only touches geometry and
        // transforms, so the material is irrelevant to the export path.
        const material = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide,
            flatShading: false,
            roughness: 0.7,
            metalness: 0.1
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;    // parts cast onto the ground shadow catcher
        mesh.receiveShadow = true;
        // Baked flat placement in sheet mm (buildExtrudedGeometry extrudes
        // z in [-t/2, +t/2]; lift so the part lies ON the sheet, z = 0..t)
        mesh.position.set(dx, dy, thickness / 2);
        mesh.name = part.name || `part${meshes.length + 1}`;

        // Viewport poses (world units, rotation-pivot frame, mesh scale 0.01):
        // flatPose = the baked mm placement seen through the old preview
        // group transform (scale 0.01, rot X -90deg, centred on the ground)
        mesh.userData.part = part;
        mesh.userData.flatPose = {
            position: new THREE.Vector3(
                (-widthMM / 2 + dx) * 0.01,
                groundY + FLAT_LIFT + (thickness / 2) * 0.01,
                (heightMM / 2 - dy) * 0.01
            ),
            quaternion: flatQuaternion.clone(),
            scale: 0.01
        };
        // If the packer used the 90-degree orientation, the geometry is in
        // the rotated frame - fold the inverse rotation into the pose so
        // the part still spawns/lands EXACTLY on the assembled chair mesh
        mesh.userData.assembledPose = compensatePackingRotation(
            computeAssembledPose(part.plane, thickness), rotated, rotCentre);

        meshes.push(mesh);
    }

    return { meshes, widthMM, heightMM, partCount: meshes.length };
}

/**
 * Bounding box of the meshes (local matrices only, same convention as
 * buildSTL), scaled by scaleFactor. Used for the model-dimensions readout.
 *
 * @param {Array<THREE.Mesh>} meshes
 * @param {number} [scaleFactor=1]
 * @returns {{widthMM: number, heightMM: number, depthMM: number}|null}
 */
export function computeMeshesBBox(meshes, scaleFactor = 1) {
    const box = new THREE.Box3();
    for (const mesh of meshes || []) {
        if (!mesh || !mesh.geometry) continue;
        mesh.updateMatrix();
        if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
        }
        box.union(mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrix));
    }
    if (box.isEmpty()) return null;
    return {
        widthMM: (box.max.x - box.min.x) * scaleFactor,
        heightMM: (box.max.y - box.min.y) * scaleFactor,
        depthMM: (box.max.z - box.min.z) * scaleFactor
    };
}
